import { createContext, useContext } from "react";
import type { LimitMessage } from "campaign-builder/state/targetingLimits";

// The Targeting step's hard gates (F8), published *up* from the TargetingProvider
// (which lives inside the step) to the builder mount (which owns the Summary CTA
// and the min-budget modal, both rendered above the provider). The provider can't
// pass state to its ancestors through normal context flow, so the mount hands a
// `report` callback down via this context and the provider calls it whenever the
// gate changes.
export interface TargetingGate {
  /**
   * The saved selection has been applied (or is known absent). Until then the
   * gate's citySelected/minMet reflect a not-yet-loaded selection — step resume
   * must wait for this rather than read targeting as incomplete.
   */
  hydrated: boolean;
  /** A city is chosen — the min/max gates are only meaningful once it is. */
  citySelected: boolean;
  /** Selected distributors (H2H) / areas (D2D) meet the city minimum (AC31). */
  minMet: boolean;
  /** Selection exceeds the channel maximum (AC32) — blocks advancing. */
  maxExceeded: boolean;
  /** The min-gate tooltip copy for the disabled CTA (null once met). */
  minTooltip: LimitMessage | null;
  /**
   * US-only: minimum met but total cost below the market minimum (AC33). Drives
   * the EDDM modal on Continue. Stays false until cost wiring (F15) supplies a
   * subtotal — the threshold helper returns false for an undefined subtotal.
   */
  belowMinBudget: boolean;
  /** "$5,000" / "$20,000" market minimum for the modal title (US only). */
  minBudgetFormatted: string | null;
  /**
   * A re-entered draft has H2H distribution dates now in the past / within the
   * lead time (AC35). Drives the Summary warning + blocks Review-and-finish.
   * D2D's launch-date case is detected at the mount from `startDate` instead.
   */
  datesOutdated: boolean;
  /** First H2H area with an outdated date — the Edit link focuses it. */
  outdatedAreaId: string | null;
  /**
   * Channel summary units derived from the current selection (F15): H2H total
   * flyers (distributors × flyer-pack), D2D total letterboxes (Σ area units).
   * The mount feeds it into the Summary card's units row.
   */
  summaryUnits: number;
  /**
   * Server-authoritative cost subtotal (pre-tax) for the current campaign + city
   * (F15). Drives the Summary "Total cost before taxes" figure and the below-market
   * -minimum check (AC33). null while the cost is loading or no city is selected.
   */
  subtotal: number | null;
}

interface TargetingGateApi {
  report: (gate: TargetingGate | null) => void;
}

export const TargetingGateContext = createContext<TargetingGateApi>({ report: () => undefined });

export const useTargetingGateReporter = () => useContext(TargetingGateContext).report;
